<template>
  <v-container class="sidebar--navigation">
    <v-dialog v-model="variation_dialog" width="900" transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="variation_dialog = false">
            <v-icon style="color: white">{{ icons.mdiClose }}</v-icon>
          </v-btn>
          <v-toolbar-title style="color: white">Variations</v-toolbar-title>
        </v-toolbar>
        <div style="padding: 50px">
          <v-data-table :headers="variation_headers" :items="variations" :items-per-page="10" class="elevation-1 mt-4">
            <template v-slot:body="{ items }">
              <tbody v-if="!loading_variations">
                <tr
                  v-for="variant in items"
                  :key="variant.id"
                  :set="(variant_attributes = JSON.parse(variant.attributes))"
                >
                  <td>
                    <span
                      v-for="(variation_value, key, index) in variant_attributes.variations"
                      :key="`variation-value-${key}-${variant.id}`"
                    >
                      &nbsp; {{ index > 0 ? '-' : '' }} &nbsp; <strong>{{ key }}: </strong>{{ variation_value }}
                    </span>
                  </td>
                  <td>{{ variant.sku }}</td>
                  <td>{{ shop.currency }} {{ variant.price }}</td>
                  <td>
                    {{ variant.stock_quantity }}
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center" style="padding: 80px 0px">
                    <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-dialog>

    <v-card class="p-1 elevation-1">
      <v-card-title class="pb-0">
        Shopify Products
        <v-spacer></v-spacer>
        <!-- <v-btn depressed color="primary" class="ml-1" @click="product_fetch_dialog = true"> Sync Product </v-btn> -->
      </v-card-title>

      <v-tabs v-model="uploadStatusTabModel" class="pt-2">
        <v-tabs-slider></v-tabs-slider>
        <v-tab
          v-for="tab in uploadStatusTabs"
          :key="tab.value"
          :href="'#tab-' + tab.value"
        >
          {{ tab.label }} &nbsp;<span class="primary--text">{{ getUploadCountForTab(tab.value) }}</span>
        </v-tab>
      </v-tabs>

      <div class="product-table-toolbar pa-4">
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.search_keyword"
              :append-icon="icons.mdiMagnify"
              label="Search Keyword"
              outlined
              dense
              hide-details
              clearable
              placeholder="Search products"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.collection_id"
              dense
              :items="collectionFilterOptions"
              item-text="title_with_products"
              item-value="id"
              label="Filter By Collection"
              @change="fetchShopifyProducts()"
              :disabled="collection_disabled"
              outlined
              hide-details
              :clearable="filters.collection_id != null"
            >
            </v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.linked_with_profile"
              dense
              :items="linkedProfileFilterOptions"
              item-text="text"
              item-value="value"
              label="Filter by Linked via Profile"
              @change="fetchShopifyProducts()"
              outlined
              hide-details
              :clearable="!!filters.linked_with_profile"
            >
            </v-select>
          </v-col>
          <v-col v-if="hasActiveFilters" cols="12" md="auto">
            <v-btn depressed outlined @click="clearFilters">Clear Filters</v-btn>
          </v-col>
        </v-row>
      </div>

      <v-data-table :headers="headers" :items="products.data" disable-pagination hide-default-footer>
        <template v-slot:body="{ items }">
          <tbody v-if="!loading_data">
            <tr v-if="!items.length">
              <td colspan="6" class="text-center" style="padding: 20px 0px">
                <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
                <p style="font-size: 25px">No Data Found!!</p>
              </td>
            </tr>
            <tr v-else v-for="item in items" :key="item.id">
              <td>
                <i style="cursor: pointer" @click="showProductDetail(item)" class="primary--text"
                  >#{{ item.shopify_id }}</i
                >
              </td>
              <td style="padding: 4px">
                <img width="50px" :src="getProductImage(item.attributes) || require('@/assets/images/misc/no-image.png')" alt="" /></td>
              <td style="max-width: 290px; font-size: 12px">
                <strong>{{ item.title }}</strong>
              </td>
              <td>{{ item.sku }}</td>
              <td>
                <div v-if="item.variations.length">
                  {{ getVariationStock(item.variations) }} in stock for {{ item.variations.length }} variants
                </div>
                <div v-else>{{ item.stock_quantity }} in stock</div>
              </td>
              <td>
                <v-chip small color="success" v-if="item.state == 1">Active</v-chip>
                <v-chip small color="warning" v-else>Inactive</v-chip>
              </td>
              <td>
                <a v-if="item.feed_id > 0" :href="`/feeds/${item.feed_id}`">
                  <v-icon small>{{ icons.mdiListStatus }}</v-icon>
                </a>
              </td>

              <td>
                  <div class="d-flex align-center" style="gap: 12px;">
                    <!-- Shopify Link -->
                    <a
                      v-if="item.shopify_handle"
                      :href="`https://${shop.specifier}/products/${item.shopify_handle}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View in Shopify"
                      class="d-inline-flex"
                    >
                      <img :src="require('@/assets/images/logos/shopify.png')" alt="View in Shopify" height="32" width="32" style="display: block; object-fit: contain;" />
                    </a>
                    <!-- Etsy Link -->
                    <a
                      v-if="getEtsyProductUrl(item)"
                      :href="getEtsyProductUrl(item)"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Edit on Etsy"
                      class="d-inline-flex"
                    >
                      <img :src="require('@/assets/images/logos/etsy.png')" alt="Edit on Etsy" height="32" width="32" style="display: block; object-fit: contain;" />
                    </a>
                  </div>
              </td>

              <td>
                <v-btn
                  title="Quick View"
                  fab
                  @click="showQuickView(item)"
                  small
                  class="table-action--button mr-2"
                >
                  <v-icon small dark>{{ icons.mdiEye }}</v-icon>
                </v-btn>
                <v-btn
                  title="Show Variations"
                  fab
                  @click="showVariations(item)"
                  small
                  v-if="item.parent_id == 0 && item.variant_id == 0"
                  class="table-action--button mr-2"
                >
                  <v-icon small dark>{{ icons.mdiChevronDown }}</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="7" class="text-center" style="padding: 80px 0px">
                <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
      <v-pagination
        v-model="products.current_page"
        class="my-4"
        @input="handlePageChange"
        :length="products.last_page"
        :total-visible="7"
      ></v-pagination>
    </v-card>

    <v-dialog v-model="product_fetch_dialog" width="500">
      <v-card>
        <v-card-title class="text-h5 grey lighten-2"> Sync Shopify Product </v-card-title>
        <div style="padding: 60px">
          <v-text-field
            v-model="syncable_product"
            label="Enter Shopify ID or Handle"
            placeholder="Enter Shopify ID or Handle"
            outlined
          ></v-text-field>
        </div>
        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="syncShopifyProducts"> Sync Now </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="quick_view_dialog" width="1400" max-width="95vw" persistent>
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          <v-img
            max-height="35"
            max-width="35"
            :src="require('@/assets/images/logos/shopify.png')"
          ></v-img>
          &nbsp;Product Comparison
          <v-spacer></v-spacer>
          <v-btn icon @click="quick_view_dialog = false">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mt-4 pa-0">
          <v-row no-gutters v-if="active_product.id !== undefined">
            <!-- Database Data -->
            <v-col cols="12" md="6" class="comparison-pane">
              <div class="comparison-pane__header blue">
                <v-icon small class="mr-1">{{ icons.mdiDatabase }}</v-icon>
                Database Data
              </div>
              <div class="comparison-pane__body pa-4 comparison-pane__body--scroll">
                <!-- Product header -->
                <div class="comparison-product-header mb-4">
                  <v-img
                    v-if="getProductImage(active_product.attributes)"
                    :src="getProductImage(active_product.attributes)"
                    max-height="120"
                    max-width="120"
                    contain
                    class="comparison-product-header__img"
                  ></v-img>
                  <v-img
                    v-else
                    :src="require('@/assets/images/logos/shopify.png')"
                    max-height="120"
                    max-width="120"
                    contain
                    class="comparison-product-header__img"
                  ></v-img>
                  <div class="comparison-product-header__info">
                    <div class="text-h6">{{ active_product.title }}</div>
                    <div class="text-caption grey--text">ID: {{ active_product.shopify_id }}</div>
                    <v-chip small :color="active_product.state === 1 ? 'success' : 'grey'" dark class="mt-1">
                      {{ active_product.state === 1 ? 'ACTIVE' : 'INACTIVE' }}
                    </v-chip>
                  </div>
                </div>
                <!-- Product details -->
                <h4 class="comparison-section-title">Product Details</h4>
                <div class="comparison-details mb-3">
                  <p><strong>Product Type:</strong> {{ dbProductType || 'N/A' }}</p>
                  <p><strong>Vendor:</strong> {{ active_product.vendor || 'N/A' }}</p>
                  <p><strong>Description:</strong></p>
                  <div class="comparison-description" v-html="active_product.description || '—'"></div>
                  <p><strong>Published at:</strong> {{ active_product.published_at || '—' }}</p>
                  <template v-if="dbHasNoVariants">
                    <p><strong>SKU:</strong> {{ active_product.sku || '—' }}</p>
                    <p><strong>Price:</strong> {{ shop.currency }} {{ active_product.price }}</p>
                    <p><strong>Stock:</strong> {{ active_product.stock_quantity != null ? active_product.stock_quantity : '—' }}</p>
                  </template>
                </div>
                <!-- Tags -->
                <h4 class="comparison-section-title">Tags</h4>
                <div class="comparison-tags mb-3">
                  <template v-if="dbTags.length">
                    <v-chip
                      v-for="tag in dbTags"
                      :key="'db-tag-' + tag"
                      small
                      class="mr-2 mb-2"
                    >{{ tag }}</v-chip>
                  </template>
                  <span v-else class="grey--text">—</span>
                </div>
                <!-- Variants -->
                <h4 class="comparison-section-title">
                  Variants ({{ (active_product.variations && active_product.variations.length) || 0 }})
                </h4>
                <v-data-table
                  v-if="active_product.variations && active_product.variations.length"
                  :headers="variation_headers"
                  :items="active_product.variations"
                  disable-pagination
                  hide-default-footer
                  dense
                  class="comparison-table mb-3"
                >
                  <template v-slot:body="{ items }">
                    <tbody>
                      <tr v-for="item in items" :key="item.id">
                        <td v-html="getVariantTitle(item)"></td>
                        <td>{{ item.sku }}</td>
                        <td>{{ shop.currency }} {{ item.price }}</td>
                        <td>{{ item.stock_quantity }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-data-table>
                <!-- Images -->
                <h4 class="comparison-section-title">Images ({{ dbImagesCount }})</h4>
                <div class="comparison-images mb-3">
                  <v-img
                    v-for="(img, idx) in getDbProductImages()"
                    :key="'db-' + idx"
                    :src="img.src || img"
                    max-height="80"
                    max-width="80"
                    contain
                    class="comparison-thumb"
                  ></v-img>
                </div>
                <!-- Videos -->
                <h4 class="comparison-section-title">Videos ({{ getDbProductVideos().length }})</h4>
                <div class="comparison-videos mb-3">
                  <template v-if="getDbProductVideos().length">
                    <video
                      v-for="(video, idx) in getDbProductVideos()"
                      :key="'db-video-' + idx"
                      :src="video.videoUrl"
                      style="max-height: 80px; max-width: 80px; object-fit: contain;"
                      class="mr-2 mb-2"
                    ></video>
                  </template>
                  <span v-else class="grey--text">—</span>
                </div>
                <!-- Options -->
                <h4 class="comparison-section-title">Options</h4>
                <div class="comparison-options mb-3">
                  <template v-if="getProductOptions(active_product.attributes).length">
                    <div v-for="option in getProductOptions(active_product.attributes)" :key="'db-' + option.name">
                      <strong>{{ option.name }}:</strong>
                      <span v-for="(value, index) in option.values" :key="value">
                        {{ value }}<span v-if="index < option.values.length - 1">, </span>
                      </span>
                    </div>
                  </template>
                  <span v-else class="grey--text">—</span>
                </div>
                <!-- Metafields -->
                <h4 class="comparison-section-title">Metafields</h4>
                <div class="comparison-metafields">
                  <template v-if="Object.keys(parseMetafields(active_product.metafields)).length">
                    <ul class="comparison-metafields-list">
                      <li v-for="(value, key) in parseMetafields(active_product.metafields)" :key="'db-meta-' + key">
                        <span v-if="typeof value === 'object' && Object.keys(value).every(k => !isNaN(k))">
                          {{ formatMetafield(value) }}
                        </span>
                        <span v-else>
                          <strong>{{ key }}:</strong> {{ formatMetafield(value) }}
                        </span>
                      </li>
                    </ul>
                  </template>
                  <span v-else class="grey--text">—</span>
                </div>
              </div>
            </v-col>
            <!-- Shopify Real-time Data -->
            <v-col cols="12" md="6" class="comparison-pane">
              <div class="comparison-pane__header green">
                <v-img
                  max-height="20"
                  max-width="20"
                  :src="require('@/assets/images/logos/shopify.png')"
                  class="mr-1"
                ></v-img>
                Shopify Real-time Data
              </div>
              <div class="comparison-pane__body pa-4 comparison-pane__body--scroll">
                <div v-if="quick_view_loading_shopify" class="d-flex flex-column align-center justify-center py-12">
                  <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                  <p class="mt-3 grey--text">Fetching product from Shopify API...</p>
                </div>
                <div v-else-if="shopifyApiError" class="pa-4">
                  <v-alert type="error" dense>{{ shopifyApiError }}</v-alert>
                </div>
                <template v-else-if="shopifyProduct">
                  <!-- Product header -->
                  <div class="comparison-product-header mb-4">
                    <v-img
                      v-if="shopifyFirstImageUrl"
                      :src="shopifyFirstImageUrl"
                      max-height="120"
                      max-width="120"
                      contain
                      class="comparison-product-header__img"
                    ></v-img>
                    <v-img
                      v-else
                      :src="require('@/assets/images/logos/shopify.png')"
                      max-height="120"
                      max-width="120"
                      contain
                      class="comparison-product-header__img"
                    ></v-img>
                    <div class="comparison-product-header__info">
                      <div class="text-h6">{{ shopifyProduct.title }}</div>
                      <div class="text-caption grey--text">ID: {{ shopifyProductId }}</div>
                      <v-chip small :color="shopifyProduct.status === 'ACTIVE' ? 'success' : 'grey'" dark class="mt-1">
                        {{ shopifyProduct.status || 'N/A' }}
                      </v-chip>
                    </div>
                  </div>
                  <!-- Product details -->
                  <h4 class="comparison-section-title">Product Details</h4>
                  <div class="comparison-details mb-3">
                    <p><strong>Product Type:</strong> {{ shopifyProduct.productType || 'N/A' }}</p>
                    <p><strong>Vendor:</strong> {{ shopifyProduct.vendor || 'N/A' }}</p>
                    <p><strong>Description:</strong></p>
                    <div class="comparison-description" v-html="shopifyProduct.descriptionHtml || '—'"></div>
                    <p><strong>Published at:</strong> {{ formatPublishedAt(shopifyProduct.updatedAt) }}</p>
                    <template v-if="shopifyDefaultTitleVariant">
                      <p><strong>SKU:</strong> {{ shopifyDefaultTitleVariant.sku || '—' }}</p>
                      <p><strong>Price:</strong> {{ shop.currency }} {{ shopifyDefaultTitleVariant.price }}</p>
                      <p><strong>Stock:</strong> {{ shopifyDefaultTitleVariant.inventoryQuantity != null ? shopifyDefaultTitleVariant.inventoryQuantity : '—' }}</p>
                    </template>
                  </div>
                  <!-- Tags -->
                  <h4 class="comparison-section-title">Tags</h4>
                  <div class="comparison-tags mb-3">
                    <template v-if="shopifyTags.length">
                      <v-chip
                        v-for="tag in shopifyTags"
                        :key="'shopify-tag-' + tag"
                        small
                        class="mr-2 mb-2"
                      >{{ tag }}</v-chip>
                    </template>
                    <span v-else class="grey--text">—</span>
                  </div>
                  <!-- Variants -->
                  <h4 class="comparison-section-title">Variants ({{ shopifyVariantsDisplayCount }})</h4>
                  <v-simple-table v-if="shopifyVariantsDisplayCount > 0" dense class="comparison-table mb-3">
                    <thead>
                      <tr>
                        <th>Attributes</th>
                        <th>SKU</th>
                        <th>Price</th>
                        <th>Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(v, i) in shopifyVariantsList" :key="v.id || i">
                        <td>{{ formatShopifyVariantOptions(v.selectedOptions) }}</td>
                        <td>{{ v.sku || '—' }}</td>
                        <td>{{ shop.currency }} {{ v.price }}</td>
                        <td>{{ v.inventoryQuantity != null ? v.inventoryQuantity : '—' }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <!-- Images -->
                  <h4 class="comparison-section-title">Images ({{ shopifyMediaImages.length }})</h4>
                  <div class="comparison-images mb-3">
                    <v-img
                      v-for="(edge, idx) in shopifyMediaImages"
                      :key="'shopify-' + idx"
                      :src="getShopifyMediaImageUrl(edge.node)"
                      max-height="80"
                      max-width="80"
                      contain
                      class="comparison-thumb"
                    ></v-img>
                  </div>
                  <!-- Videos -->
                  <h4 class="comparison-section-title">Videos ({{ shopifyMediaVideos.length }})</h4>
                  <div class="comparison-videos mb-3">
                    <template v-if="shopifyMediaVideos.length">
                      <v-img
                        v-for="(edge, idx) in shopifyMediaVideos"
                        :key="'shopify-video-' + idx"
                        :src="getShopifyMediaVideoUrl(edge.node)"
                        max-height="80"
                        max-width="80"
                        contain
                        class="comparison-thumb"
                      ></v-img>
                    </template>
                    <span v-else class="grey--text">—</span>
                  </div>
                  <!-- Options -->
                  <h4 class="comparison-section-title">Options</h4>
                  <div class="comparison-options mb-3">
                    <template v-if="shopifyOptions.length">
                      <div v-for="opt in shopifyOptions" :key="opt.name">
                        <strong>{{ opt.name }}:</strong>
                        <span v-for="(val, index) in opt.values" :key="val">
                          {{ val }}<span v-if="index < opt.values.length - 1">, </span>
                        </span>
                      </div>
                    </template>
                    <span v-else class="grey--text">—</span>
                  </div>
                  <!-- Metafields -->
                  <h4 class="comparison-section-title">Metafields</h4>
                  <div class="comparison-metafields">
                    <template v-if="shopifyMetafieldsList.length">
                      <ul class="comparison-metafields-list">
                        <li v-for="(node, idx) in shopifyMetafieldsList" :key="'shopify-meta-' + (node.id || idx)">
                          <strong>{{ node.key }}:</strong> {{ node.value }}
                        </li>
                      </ul>
                    </template>
                    <span v-else class="grey--text">—</span>
                  </div>
                </template>
                <div v-else class="text-center grey--text py-8">No data</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>


<script>
import store from '@/store'
import router from '@/router'
import { mdiChevronDown, mdiClose, mdiListStatus, mdiEye, mdiCheckCircle, mdiCancel, mdiDatabase } from '@mdi/js'
import { fetchShopifyProduct } from '@/utils/shopifyGraphql' // uses SHOPIFY_API_VERSION by default

export default {
  props: {
    collection: {
      type: [String, Number],
      default: '0',
    },
  },
  data() {
    return {
      loading_data: false,
      shop: null,
      filters: {
        collection_id: null,
        search_keyword: null,
        linked_with_profile: null,
      },
      linkedProfileFilterOptions: [
        { text: 'All', value: null },
        { text: 'Linked Products via Profile', value: 'linked' },
        { text: 'Linked Products without Profile', value: 'unlinked' },
      ],
      product_fetch_dialog: false,
      headers: [
        {
          text: 'Shopify ID',
          value: 'remote_id',
        },
        {
          text: 'Image',
        },
        {
          text: 'Title',
          value: 'title',
        },
        {
          text: 'SKU',
          value: 'sku',
        },
        {
          text: 'Inventory',
        },
        {
          text: 'Status',
          value: 'state',
        },
        {
          text: 'Feed',
          value: 'feed_id',
        },
        {
          text: 'View on',
          value: 'view_on',
          sortable: false
        },
        {
          text: 'Actions',
        },
      ],
      icons: {
        mdiChevronDown,
        mdiClose,
        mdiListStatus,
        mdiEye,
        mdiCheckCircle,
        mdiCancel,
        mdiDatabase
      },
      products: {
        current_page: 1,
        data: [],
        per_page: 20,
        upload_counts: { all: 0, uploaded: 0, not_uploaded: 0 },
      },
      uploadStatusTab: 'all',
      uploadStatusTabs: [
        { label: 'All', value: 'all' },
        { label: 'Uploaded', value: 'uploaded' },
        { label: 'Not Uploaded', value: 'not_uploaded' },
      ],

      variation_dialog: false,
      loading_variations: false,
      variations: [],
      collections: [],
      variation_headers: [
        {
          text: 'Attributes',
        },
        { text: 'SKU' },
        { text: 'Price' },
        { text: 'Stock' },
      ],

      active_product: {},

      collection_disabled: false,

      syncable_product: '',
      syncing_product: false,
      quick_view_dialog: false,
      quick_view_loading_shopify: false,
      shopify_realtime_data: null,
    }
  },

  created() {
    this.debouncedSearch = require('lodash/debounce')(() => {
      this.products.current_page = 1
      this.fetchShopifyProducts()
    }, 350)
  },
  async mounted() {
    if (this.collection != 0) {
      this.collection_disabled = true
      this.filters.collection_id = parseInt(this.collection)
    }
    this.shop = store.getters.loggedInShop
    await this.fetchShopifyCollections()

    this.fetchShopifyProducts()
  },

  methods: {
    getEtsyProductUrl(item) {
      const etsyProduct = item?.etsy_product;

      if (!etsyProduct) return null;

      const raw = etsyProduct.upload_result;

      if (raw == null) return null;

      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const url = parsed?.url;

        return typeof url === 'string' && url.length > 0 ? url : null;
      } catch (e) {
        return null;
      }
    },

    async fetchShopifyProducts() {
      try {
        this.loading_data = true
        const params = {
          page: this.products.current_page,
          search_keyword: this.filters.search_keyword || undefined,
          per_page: this.products.per_page,
          collection_id: this.filters.collection_id || undefined,
          linked_with_profile: this.filters.linked_with_profile || undefined,
          upload_counts: 1,
        }
        if (this.uploadStatusTab === 'uploaded') {
          params.upload_status = 1
        } else if (this.uploadStatusTab === 'not_uploaded') {
          params.upload_status = 2
        }
        let response = await axios.get(`admin/shops/${this.shop.id}/get-shopify-products`, { params })
        if (response.data.success) {
          const productsData = response.data.products
          this.products = { ...this.products, ...productsData }
          if (response.data.upload_counts) {
            this.products.upload_counts = response.data.upload_counts
          }
          this.is_first = false
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.loading_data = false
      }
    },

    async syncShopifyProducts() {
      try {
        this.loading_data = true
        let response = await axios.get(`http://localhost:8081/index.php?option=com_rapidcart&task=servshopify.processSingleProduct`, {
          params: {
          //   page: this.products.current_page,
          //   search_keyword: this.filters.search_keyword,
          //   per_page: this.products.per_page,
          //   collection_id: this.filters.collection_id,
          },
        })

        dd(response)

        // if (response.data.success) {
        //   this.products = response.data.products
        //   this.is_first = false
        // }
      } catch (e) {
        console.log(e)
      } finally {
        this.loading_data = false
      }
    },

    async fetchShopifyCollections() {
      try {
        this.loading_data = true
        let response = await axios.get(`admin/shops/${this.shop.id}/get-shopify-collections`)
        if (response.data.success) {
          this.collections = response.data.data
        }
      } catch (e) {
      } finally {
        this.loading_data = false
      }
    },
    getProductImage(attributes) {
      attributes = JSON.parse(attributes)
      let img_url = ''
      if (attributes.images) {
        if (Array.isArray(attributes.images)) {
          if (attributes.images[0]) {
            img_url = attributes.images[0].src
          }
        } else {
          img_url = attributes?.image_url
        }
      }
      return img_url
    },

    getVariationStock(variations) {
      let quantity = 0
      variations.forEach(function (variant) {
        quantity += variant.stock_quantity
      })

      return quantity
    },

    async showVariations(product_detail) {
      this.variations = product_detail.variations
      this.variation_dialog = true

      // try {
      //   this.variation_dialog = true
      //   this.loading_variations = true
      //   let response = await axios.get(`admin/shops/${this.shop.id}/shopify-product/${product_id}/variations`)
      //   if (response.data.success) {
      //     this.variations = response.data.variations
      //   }
      // } catch (e) {
      // } finally {
      //   this.loading_variations = false
      // }
    },

    handlePageChange(value) {
      this.fetchShopifyProducts()
    },

    getUploadCountForTab(tabValue) {
      const counts = this.products.upload_counts || { all: 0, uploaded: 0, not_uploaded: 0 }
      if (tabValue === 'all') return counts.all ?? 0
      if (tabValue === 'uploaded') return counts.uploaded ?? 0
      if (tabValue === 'not_uploaded') return counts.not_uploaded ?? 0
      return 0
    },

    setUploadStatusTab(tabValue) {
      this.uploadStatusTab = tabValue
      this.products.current_page = 1
      this.fetchShopifyProducts()
    },

    clearFilters() {
      if (!this.collection_disabled) {
        this.filters.collection_id = null
      }
      this.filters.search_keyword = null
      this.filters.linked_with_profile = null
      this.products.current_page = 1
      this.fetchShopifyProducts()
    },

    showProductDetail(product) {
        router.push(`/shopify-products/${product.id}`);

      // this.active_product = product
      // this.product_detail_dialog = true
    },

    getActiveProductImages() {
      if (!this.active_product) {
        return
      }

      if (this.active_product.variations && this.active_product.variations.length) {
        let first_variation = this.active_product.variations[0]
        let product_attributes = JSON.parse(first_variation.attributes)

        return product_attributes.image_url
      } else {
        let product_attributes = JSON.parse(this.active_product.attributes)
        return product_attributes.image_url
      }
    },

    async showQuickView(product) {
      this.active_product = product
      this.shopify_realtime_data = null
      this.quick_view_dialog = true
      this.quick_view_loading_shopify = true
      try {
        const result = await fetchShopifyProduct(this.shop.id, product.shopify_id)
        this.shopify_realtime_data = result
      } catch (err) {
        this.shopify_realtime_data = {
          success: false,
          error: err.response?.data?.message || err.message || 'Failed to fetch Shopify product',
          details: err.response?.data,
        }
      } finally {
        this.quick_view_loading_shopify = false
      }
    },

    parseAttributes(attributes) {
      try {
        return JSON.parse(attributes);
      } catch (error) {
        console.error('Error parsing attributes:', error);
        return {};
      }
    },

    capitalizeFirstLetters(str) {
      return str
        .split(', ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(', ');
    },

    parseMetafields(metafields) {
      if (!metafields) return {};

      let parsed = {};
      try {
        parsed = JSON.parse(metafields);
      } catch (e) {
        console.error('Error parsing metafields:', e);
        return {};
      }

      // If parsed is an array, convert to object with numeric keys
      if (Array.isArray(parsed)) {
        const obj = {};
        parsed.forEach((v, i) => (obj[i] = v));
        parsed = obj;
      }

    return parsed;
  },

    formatMetafield(value) {
      if (Array.isArray(value)) {
        return value.map(v => this.formatMetafield(v)).join(', ');
      }

      if (typeof value === 'object' && value !== null) {
        // Special case: object has 'value' and 'unit' → show nicely
        if ('value' in value && 'unit' in value) {
          return `${value.value} ${value.unit.toLowerCase()}`;
        }

        // Only numeric keys starting from 0 → show values only
        const keys = Object.keys(value);
        const isArrayLike = keys.every(k => !isNaN(k));
        if (isArrayLike) {
          return keys.map(k => this.formatMetafield(value[k])).join(', ');
        }

        // Otherwise show key: value recursively
        return Object.entries(value)
          .map(([k, v]) => `${k}: ${this.formatMetafield(v)}`)
          .join(', ');
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();

        // If string is JSON array/object
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
            (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
          try {
            const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
            return this.formatMetafield(parsed); // recursive
          } catch (e) {
            return value;
          }
        }

        return value;
      }

      return value ?? '';
    },

    getVariantTitle(item) {
      try {
        const variations = JSON.parse(item.attributes).variations;
        return Object.entries(variations)
          .map(([key, value]) => `${key}: ${value}`)
          .join('<br>');
      } catch (e) {
        return JSON.parse(item.attributes).title;
      }
    },

    getProductOptions(attributes) {
      try {
        const parsed = JSON.parse(attributes);
        if (!parsed.options || !Array.isArray(parsed.options)) return [];

        return parsed.options.map(option => ({
          name: option.name,
          values: option.values || []
        }));
      } catch (e) {
        console.error('Error parsing options:', e);
        return [];
      }
    },

    getDbProductImages() {
      if (!this.active_product || !this.active_product.attributes) return []
      try {
        const attrs = this.parseAttributes(this.active_product.attributes)
        const images = attrs.images
        if (!images || !Array.isArray(images)) return []
        return images.map(img => (typeof img === 'string' ? img : { src: img?.src || img?.url }))
      } catch (e) {
        return []
      }
    },

    getDbProductVideos() {
      if (!this.active_product || !this.active_product.shopify_videos) return []
      try {
        const videos = this.active_product.shopify_videos
        if (!Array.isArray(videos)) return []
        // Parse the video JSON field to extract video info
        return videos.map(v => {
          let videoData = {}
          try {
            videoData = typeof v.video === 'string' ? JSON.parse(v.video) : v.video
          } catch (e) {
            console.error('Error parsing video data:', e)
          }
          return {
            ...v,
            videoData,
            videoUrl: videoData?.sources?.[0]?.url || ''
          }
        })
      } catch (e) {
        return []
      }
    },

    getDbVideoPreview(video) {
      // Try to find matching video in Shopify real-time data to get preview image
      if (this.shopifyMediaVideos.length && video.videoData?.id) {
        const matchingVideo = this.shopifyMediaVideos.find(
          edge => edge.node.id === video.videoData.id
        )
        if (matchingVideo) {
          return this.getShopifyMediaVideoUrl(matchingVideo.node)
        }
      }
      // Fallback: return video URL - the video player will show first frame
      return video.videoUrl
    },

    getShopifyMediaImageUrl(node) {
      if (!node) return ''
      return node.image?.url || node.preview?.image?.url || ''
    },

    getShopifyMediaVideoUrl(node) {
      if (!node) return ''
      // For video content type, return the preview image URL
      return node.preview?.image?.url || ''
    },

    formatShopifyVariantOptions(selectedOptions) {
      if (!selectedOptions || !selectedOptions.length) return '—'
      return selectedOptions.map(o => `${o.name}: ${o.value}`).join(', ')
    },

    /** Format ISO date (e.g. 2026-03-11T17:10:38Z) to match DB format: 2026-03-11 17:10:38 */
    formatPublishedAt(isoDate) {
      if (!isoDate) return '—'
      const s = String(isoDate).trim().replace('T', ' ').replace(/\.\d{3}Z?$/i, '').replace('Z', '')
      return s || '—'
    },

  },
  computed: {
    uploadStatusTabModel: {
      get() {
        return 'tab-' + this.uploadStatusTab
      },
      set(tabValue) {
        const value = (tabValue || '').replace(/^#?tab-/, '')
        if (value) this.setUploadStatusTab(value)
      },
    },
    hasActiveFilters() {
      const hasCollection = !this.collection_disabled &&
        this.filters.collection_id != null && this.filters.collection_id !== ''
      const hasSearch = this.filters.search_keyword && this.filters.search_keyword.trim()
      const hasLinkedProfile = this.filters.linked_with_profile != null && this.filters.linked_with_profile !== ''
      return !!(hasCollection || hasSearch || hasLinkedProfile)
    },
    collectionFilterOptions() {
      return [{ id: null, title_with_products: 'All' }, ...(this.collections || [])]
    },
    // Comparison: Shopify API response
    shopifyProduct() {
      if (!this.shopify_realtime_data || !this.shopify_realtime_data.success) return null
      return this.shopify_realtime_data.data?.product || null
    },
    shopifyApiError() {
      if (!this.shopify_realtime_data) return null
      if (this.shopify_realtime_data.success) return null
      return this.shopify_realtime_data.error || 'Failed to load Shopify data'
    },
    shopifyProductId() {
      if (!this.shopifyProduct || !this.shopifyProduct.id) return '—'
      const gid = this.shopifyProduct.id
      const match = gid.match(/\/(\d+)$/)
      return match ? match[1] : gid
    },
    shopifyFirstImageUrl() {
      if (!this.shopifyProduct || !this.shopifyProduct.media?.edges?.length) return ''
      const node = this.shopifyProduct.media.edges[0].node
      return node?.image?.url || node?.preview?.image?.url || ''
    },
    shopifyVariantsList() {
      if (!this.shopifyProduct || !this.shopifyProduct.variants?.edges) return []
      return this.shopifyProduct.variants.edges.map(e => e.node)
    },
    shopifyMediaImages() {
      if (!this.shopifyProduct || !this.shopifyProduct.media?.edges) return []
      return this.shopifyProduct.media.edges.filter(edge => edge.node.mediaContentType === 'IMAGE')
    },
    shopifyMediaVideos() {
      if (!this.shopifyProduct || !this.shopifyProduct.media?.edges) return []
      return this.shopifyProduct.media.edges.filter(edge => edge.node.mediaContentType === 'VIDEO')
    },
    shopifyOptions() {
      if (!this.shopifyProduct || !this.shopifyVariantsList.length) return []
      const map = new Map()
      this.shopifyVariantsList.forEach(v => {
        (v.selectedOptions || []).forEach(opt => {
          if (!map.has(opt.name)) map.set(opt.name, new Set())
          map.get(opt.name).add(opt.value)
        })
      })
      return Array.from(map.entries()).map(([name, values]) => ({
        name,
        values: Array.from(values),
      }))
    },
    shopifyMetafieldsList() {
      if (!this.shopifyProduct || !this.shopifyProduct.metafields?.edges) return []
      return this.shopifyProduct.metafields.edges.map(e => e.node)
    },
    // Comparison: Database
    dbProductType() {
      if (!this.active_product || !this.active_product.attributes) return ''
      const attrs = this.parseAttributes(this.active_product.attributes)
      return attrs.product_type || ''
    },
    dbImagesCount() {
      const imgs = this.getDbProductImages()
      return imgs.length
    },
    dbTags() {
      if (!this.active_product || !this.active_product.tags) return []
      const raw = this.active_product.tags
      if (typeof raw !== 'string') return Array.isArray(raw) ? raw : []
      return raw.split(',').map(t => t.trim()).filter(Boolean)
    },
    dbHasNoVariants() {
      if (!this.active_product) return false
      return !this.active_product.variations || this.active_product.variations.length === 0
    },
    shopifyDefaultTitleVariant() {
      if (!this.shopifyVariantsList || this.shopifyVariantsList.length !== 1) return null
      const single = this.shopifyVariantsList[0]
      return single.title === 'Default Title' ? single : null
    },
    shopifyVariantsDisplayCount() {
      if (this.shopifyDefaultTitleVariant) return 0
      return this.shopifyVariantsList ? this.shopifyVariantsList.length : 0
    },
    shopifyTags() {
      if (!this.shopifyProduct || !this.shopifyProduct.tags) return []
      return Array.isArray(this.shopifyProduct.tags) ? this.shopifyProduct.tags : []
    },
  },
  watch: {
    'filters.search_keyword'() {
      this.debouncedSearch()
    },
  },
}
</script>

<style scoped>
.product-table-toolbar {
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.comparison-pane {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.comparison-pane:last-child {
  border-right: none;
}
.comparison-pane__header {
  padding: 10px 16px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
}
.comparison-pane__header.blue {
  background: #1976d2;
}
.comparison-pane__header.green {
  background: #2e7d32;
}
.comparison-pane__body--scroll {
  max-height: 70vh;
  overflow-y: auto;
}
.comparison-product-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.comparison-product-header__img {
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.comparison-product-header__info {
  min-width: 0;
}
.comparison-section-title {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 20px 0 10px 0;
  color: rgba(0, 0, 0, 0.87);
}
.comparison-details p {
  margin: 4px 0;
  font-size: 0.875rem;
}
.comparison-description {
  font-size: 0.875rem;
  max-height: 120px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px;
  margin-top: 4px;
}
.comparison-description >>> p {
  margin: 0 0 6px 0;
}
.comparison-table {
  font-size: 0.8rem;
}
.comparison-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.comparison-thumb {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}
.comparison-options {
  font-size: 0.875rem;
}
.comparison-options div {
  margin-bottom: 4px;
}
.comparison-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.comparison-metafields-list {
  margin: 0;
  padding-left: 20px;
  font-size: 0.875rem;
}
.comparison-metafields-list li {
  margin-bottom: 4px;
}
</style>
